/**
 * The partner-facing case page (Lloyd, Jul 21: "Let's do the per-case links").
 *
 * No login. Anyone holding the URL sees this, so it shows only where the case has got to and how it
 * ended — the server decides that, in caseShareService. Nothing here requests the client's name,
 * contact details or documents, and nothing should be added that does.
 *
 * Since 15 Oct 2026 it renders the server's partner facts (backend partnerCaseFacts.ts), the same
 * ones the automatic CDL reply sends: Lloyd's Share list as far as the system stores it. The page
 * renders text it is given and derives nothing from internal status names, so it cannot reveal a
 * pending offer the server has already kept out.
 */

import React, { useEffect, useState } from 'react';
import { useParams } from 'react-router-dom';
import {
  Box,
  Card,
  CardContent,
  CircularProgress,
  Container,
  Divider,
  Typography,
} from '@mui/material';
/** Twin of PublicCaseProgressDto in backend/src/utils/caseShare/caseShareService.ts. */
interface CaseProgress {
  reference: string;
  /** The partner sentence for the stage. Never the internal status name. */
  stage: string;
  status: 'pending' | 'closed';
  courtName: string | null;
  county: string | null;
  causeNumber: string | null;
  /** Null on imported Clio cases, whose number is not a citation number. */
  citationNumber: string | null;
  nextSetting: { date: string; dateText: string; isTrial: boolean } | null;
  /** Closed cases only. */
  disposition: { summary: string; terms: string[] } | null;
  isClosed: boolean;
}

const STATUS_LABELS: Record<CaseProgress['status'], string> = {
  pending: 'Pending',
  closed: 'Closed',
};

/** One labelled fact, left out entirely when the case does not have it. */
const Fact: React.FC<{ label: string; value: string | null }> = ({ label, value }) =>
  value ? (
    <Box sx={{ mt: 1.5 }}>
      <Typography variant="body2" color="text.secondary">
        {label}
      </Typography>
      <Typography variant="body1">{value}</Typography>
    </Box>
  ) : null;

const CaseProgressView: React.FC = () => {
  const { token } = useParams<{ token: string }>();
  const [progress, setProgress] = useState<CaseProgress | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    let cancelled = false;

    const load = async (): Promise<void> => {
      if (!token) {
        setError('This link is not valid.');
        setLoading(false);
        return;
      }

      try {
        const response = await fetch(
          `${process.env.API_HOST || ''}/api/v1/case-progress/${encodeURIComponent(token)}`,
        );
        const data = await response.json().catch(() => ({}));

        if (cancelled) return;

        if (!response.ok) {
          // 403 means the link is real but the client has not given permission yet — a different
          // thing from a dead link, and the partner needs to know which so they chase the right one.
          setError(
            data.message ||
              (response.status === 403
                ? 'This case cannot be shown yet. Please contact the firm.'
                : 'This link is no longer valid.'),
          );
        } else {
          setProgress(data as CaseProgress);
        }
      } catch {
        if (!cancelled) setError('Could not load this case. Please try again later.');
      } finally {
        if (!cancelled) setLoading(false);
      }
    };

    load();
    return () => {
      cancelled = true;
    };
  }, [token]);

  return (
    <Container maxWidth="md" sx={{ py: 6 }}>
      <Typography variant="h5" component="h1" sx={{ mb: 1 }}>
        Case progress
      </Typography>
      <Typography variant="body2" color="text.secondary" sx={{ mb: 3 }}>
        The Law Offices of Dr. Lloyd A. Newton
      </Typography>

      <Card variant="outlined">
        <CardContent sx={{ p: 3 }}>
          {loading ? (
            <Box sx={{ display: 'flex', justifyContent: 'center', py: 4 }}>
              <CircularProgress />
            </Box>
          ) : error ? (
            <Typography variant="body1" color="text.secondary">
              {error}
            </Typography>
          ) : progress ? (
            <>
              <Typography variant="subtitle1" sx={{ fontWeight: 600 }}>
                {progress.stage}
              </Typography>
              {progress.disposition && (
                <Typography variant="body1" sx={{ mt: 0.5 }}>
                  {progress.disposition.summary}
                </Typography>
              )}

              {/*
                The eight-step bar is gone from this page. It lit "We have an offer from the court"
                for anyone holding the link, and Lloyd does not share pending offers (14 Oct 2026).
              */}
              <Fact label="Status" value={STATUS_LABELS[progress.status]} />
              <Fact label="Court" value={progress.courtName} />
              <Fact label="County" value={progress.county} />
              <Fact label="Cause number" value={progress.causeNumber} />
              <Fact label="Citation number" value={progress.citationNumber} />
              <Fact
                label="Next court setting"
                value={
                  progress.nextSetting
                    ? `${progress.nextSetting.dateText}${progress.nextSetting.isTrial ? ' (trial)' : ''}`
                    : null
                }
              />

              {progress.disposition && progress.disposition.terms.length > 0 && (
                <Box sx={{ mt: 1.5 }}>
                  <Typography variant="body2" color="text.secondary">
                    Accepted terms
                  </Typography>
                  {progress.disposition.terms.map((term) => (
                    <Typography key={term} variant="body1">
                      {term}
                    </Typography>
                  ))}
                </Box>
              )}

              <Divider sx={{ my: 3 }} />

              <Typography variant="body2" color="text.secondary">
                Our reference: {progress.reference}
              </Typography>

              <Typography variant="caption" color="text.secondary" sx={{ mt: 2, display: 'block' }}>
                This page shows case progress only. For anything else, please contact the firm.
              </Typography>
            </>
          ) : null}
        </CardContent>
      </Card>
    </Container>
  );
};

export default CaseProgressView;
